import { type SelectOption } from '@inmotionnow/momentum-components-react'

import { type UserType, UserTypeId } from './userApi'

export enum UserStatusId {
  Disabled = 0,
  Active = 1,
}

const USER_TYPE_ID_TO_VALUE: Record<UserTypeId, UserType> = {
  [UserTypeId.Admin]: 'admin',
  [UserTypeId.TeamMember]: 'team_member',
}

export const userTypeIdToValue = (
  id: UserTypeId | null,
): UserType | undefined => (id !== null ? USER_TYPE_ID_TO_VALUE[id] : undefined)

const USER_TYPE_VALUE_TO_ID: Record<UserType, UserTypeId> = {
  admin: UserTypeId.Admin,
  team_member: UserTypeId.TeamMember,
}

export const userTypeValueToId = (value: UserType): UserTypeId =>
  USER_TYPE_VALUE_TO_ID[value]

export enum LocaleId {
  AccountDefault = 0,
  English = 1,
  French = 2,
  Dutch = 3,
  German = 4,
}

const LOCALE_ID_TO_VALUE: Record<LocaleId, string> = {
  // 'us' is a backend sentinel meaning "inherit the account's Keycloak defaultLocale" — not a locale code
  [LocaleId.AccountDefault]: 'us',
  [LocaleId.English]: 'en',
  [LocaleId.French]: 'fr',
  [LocaleId.Dutch]: 'nl',
  [LocaleId.German]: 'de',
}

export const localeIdToValue = (id: LocaleId): string => LOCALE_ID_TO_VALUE[id]

export const generateLocaleOptions = (
  translate: (key: string) => string,
): SelectOption[] => [
  {
    value: LocaleId.AccountDefault,
    displayText: translate('LOCALE_ACCOUNT_DEFAULT'),
  },
  { value: LocaleId.English, displayText: translate('LOCALE_ENGLISH') },
  { value: LocaleId.French, displayText: translate('LOCALE_FRENCH') },
  { value: LocaleId.Dutch, displayText: translate('LOCALE_DUTCH') },
  { value: LocaleId.German, displayText: translate('LOCALE_GERMAN') },
]

export const FALLBACK_TIMEZONE = 'UTC'

const BROWSER_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone

const listSupportedTimezones = (): string[] => {
  // Intl.supportedValuesOf is ES2022 and this project compiles against the ES2020
  // libs, so it is reached through a narrowed cast. Older browsers that lack it
  // fall back to the single-entry list below rather than breaking the form.
  const intl = Intl as typeof Intl & {
    supportedValuesOf?: (key: 'timeZone') => string[]
  }
  return intl.supportedValuesOf?.('timeZone') ?? []
}

/**
 * IANA timezone names the Timezone field offers. Sourced from the browser's own
 * tz database instead of a hardcoded list, because Intl's canonical zones are a
 * subset of the zoneinfo names GIM's backend validates against — so every value
 * selectable here is one the backend accepts. Before KPS-513 the field was free
 * text, and a typo produced a backend rejection with no message in the UI.
 */
const TIMEZONE_NAMES: readonly string[] = (() => {
  const names = new Set(listSupportedTimezones())
  names.add(FALLBACK_TIMEZONE)
  // Keep the browser's own zone selectable even when Intl's canonical list omits
  // it (deprecated aliases such as 'Asia/Calcutta', which zoneinfo still carries):
  // the form pre-selects that zone, and dropping it would silently move the new
  // user to a different timezone than the one shown.
  if (BROWSER_TIMEZONE) {
    names.add(BROWSER_TIMEZONE)
  }
  return [...names].sort()
})()

export const generateTimezoneOptions = (): SelectOption[] =>
  TIMEZONE_NAMES.map((zone, index) => ({ value: index, displayText: zone }))

export const timezoneIdToValue = (id: number): string =>
  TIMEZONE_NAMES[id] ?? FALLBACK_TIMEZONE

/**
 * Index of an IANA zone in the option list, falling back to UTC's index for a
 * value the list doesn't carry — a numeric Select can't represent "no match",
 * and showing UTC is more predictable than leaving the control blank.
 */
export const timezoneValueToId = (zone: string): number => {
  const index = TIMEZONE_NAMES.indexOf(zone)
  return index === -1 ? TIMEZONE_NAMES.indexOf(FALLBACK_TIMEZONE) : index
}

export const generateUserTypeOptions = (
  translate: (key: string) => string,
): SelectOption[] => [
  {
    value: -1,
    displayText: translate('CHOOSE_USER_TYPE'),
    isDisabled: true,
  },
  { value: UserTypeId.Admin, displayText: translate('ADMIN') },
  {
    value: UserTypeId.TeamMember,
    displayText: translate('TEAM_MEMBER'),
  },
]
